import React, { useEffect, useMemo, useRef, useState } from 'react'
import { DifferentSectionLogo } from './DifferentSectionLogo'

export const DataCylinder: React.FC<{ isDark?: boolean }> = ({ isDark = true }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showStreams, setShowStreams] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsOpen(true)
          setTimeout(() => setShowStreams(true), 800)
          observer.disconnect()
        }
      },
      { threshold: 0.3 }
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const numLines = 100
  const radius = 140
  const height = 1000

  const { stars1, stars2 } = useMemo(() => {
    const generateStars = (count: number) => {
      return Array.from({ length: count })
        .map(() => {
          const x = Math.random() * 100
          const y = Math.random() * 100
          const size = Math.random() * 2 + 1
          const opacity = 0.4 + Math.random() * 0.6
          const color = isDark ? '255,255,255' : '0,0,0'
          return `radial-gradient(${size}px ${size}px at ${x}vw ${y}vh, rgba(${color},${opacity}), transparent)`
        })
        .join(', ')
    }

    return {
      stars1: generateStars(200),
      stars2: generateStars(200),
    }
  }, [isDark])

  const innerRimColor = isDark ? '#fff' : '#18181b'
  const logoColorClass = isDark
    ? 'text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.8)]'
    : 'text-zinc-900 drop-shadow-[0_0_15px_rgba(0,0,0,0.5)]'
  const particleCoreColor = isDark ? 'bg-white shadow-[0_0_10px_white]' : 'bg-zinc-900 shadow-[0_0_10px_#18181b]'
  const textColorClass = isDark ? 'text-white' : 'text-zinc-900'

  return (
    <div ref={containerRef} className='relative w-full h-[500px]'>
      <div className='absolute inset-0 flex items-center justify-center overflow-hidden [perspective:1200px] [mask-image:radial-gradient(circle_at_center,white_30%,transparent_80%)]'>
        <div
          className='absolute inset-0'
          style={{
            backgroundImage: stars1,
            backgroundAttachment: 'fixed',
            backgroundSize: '100vw 100vh',
            animation: 'twinkle 4s ease-in-out infinite',
          }}
        />
        <div
          className='absolute inset-0'
          style={{
            backgroundImage: stars2,
            backgroundAttachment: 'fixed',
            backgroundSize: '100vw 100vh',
            animation: 'twinkle 5s ease-in-out infinite',
            animationDelay: '-2s',
          }}
        />

        <div
          className='absolute top-1/2 left-1/2 [transform-style:preserve-3d]'
          style={{
            transform: 'translate(-50%, -50%) rotateX(70deg) rotateZ(-45deg) translateY(-150px)',
          }}
        >
          <div
            className='absolute top-0 left-0 rounded-full border-[6px] border-[#0052FF] shadow-[0_0_60px_#0052FF,inset_0_0_60px_#0052FF]'
            style={{
              width: `${radius * 2}px`,
              height: `${radius * 2}px`,
              transform: `translate(-50%, -50%) rotateX(90deg) scale(${isOpen ? 1 : 0})`,
              opacity: isOpen ? 1 : 0,
              transition: 'all 1.2s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          />
          <div
            className={`absolute top-0 left-0 rounded-full border-[2px] ${isDark ? 'border-white' : 'border-zinc-900'}`}
            style={{
              width: `${radius * 2 - 6}px`,
              height: `${radius * 2 - 6}px`,
              transform: `translate(-50%, -50%) rotateX(90deg) scale(${isOpen ? 1 : 0})`,
              opacity: isOpen ? 1 : 0,
              boxShadow: `0 0 20px ${innerRimColor}, inset 0 0 20px ${innerRimColor}`,
              transition: 'all 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.1s',
            }}
          />

          <div
            className='absolute top-0 left-0 flex items-center justify-center [transform-style:preserve-3d]'
            style={{
              transform: 'translate(-50%, -50%) rotateZ(45deg) rotateX(-70deg)',
              opacity: isOpen ? 1 : 0,
              transition: 'opacity 1s ease-in-out 0.5s',
            }}
          >
            <div
              style={{
                animation: isOpen ? 'emerge-logo 2s cubic-bezier(0.16, 1, 0.3, 1) forwards' : 'none',
              }}
            >
              <div
                style={{
                  animation: isOpen ? 'float-logo 6s ease-in-out infinite' : 'none',
                }}
              >
                <DifferentSectionLogo className={`w-24 h-24 ${logoColorClass}`} blinkSpeed='1s' />
              </div>
            </div>
          </div>

          {Array.from({ length: numLines }).map((_, i) => {
            const angle = (i * 360) / numLines
            const delay = Math.random() * 2
            const duration = 0.8 + Math.random() * 1.5
            const hasBrightParticle = Math.random() > 0.3
            const hasStream = Math.random() > 0.7

            return (
              <div
                key={i}
                className='absolute top-0 left-0 w-[1px] origin-top [transform-style:preserve-3d]'
                style={{
                  height: `${height}px`,
                  transform: `rotateY(${angle}deg) translateZ(${radius}px)`,
                }}
              >
                {hasStream && (
                  <div
                    className='absolute top-0 left-1/2 -translate-x-1/2 w-[2px] bg-[#0052FF] rounded-full shadow-[0_0_15px_#0052FF]'
                    style={{
                      height: `${40 + Math.random() * 80}px`,
                      animation: showStreams ? `stream-fall ${duration}s linear infinite` : 'none',
                      animationDelay: `${delay}s`,
                      willChange: 'transform',
                      opacity: 0,
                    }}
                  >
                    {hasBrightParticle && (
                      <div
                        className={`absolute top-0 left-1/2 -translate-x-1/2 w-[2px] rounded-full ${particleCoreColor}`}
                        style={{ height: '30%' }}
                      />
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className='absolute bottom-4 left-18 max-w-[310px] z-10'>
        <p className={`${textColorClass} font-sans text-[16px] leading-snug opacity-80`}>
          Graviton feels like it warped here from Outer Space.
        </p>
      </div>

      <style>{`
        @keyframes stream-fall {
          0% {
            transform: translateY(0) translateZ(0);
            opacity: 1;
          }
          80% {
            opacity: 1;
          }
          100% {
            transform: translateY(${height}px) translateZ(0);
            opacity: 0;
          }
        }
        @keyframes twinkle {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 1;
          }
        }
        @keyframes emerge-logo {
          0% {
            transform: translateZ(-200px) scale(0);
          }
          100% {
            transform: translateZ(50px) scale(1);
          }
        }
        @keyframes float-logo {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-15px);
          }
        }
      `}</style>
    </div>
  )
}
